import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Logo3D from "@/components/hud/Logo3D";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function BillingReturn() {
  const [status, setStatus] = useState("checking"); // checking | success | failed | timeout
  const [tierName, setTierName] = useState("");
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const polled = useRef(false);

  useEffect(() => {
    if (polled.current) return;
    polled.current = true;

    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) { setStatus("failed"); return; }

    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      if (attempts > 8) { setStatus("timeout"); return; }
      try {
        const res = await api.get(`/billing/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setStatus("success");
          setTierName(res.data.user?.tier_name || "");
          await refresh();
          return;
        }
        if (res.data.status === "expired") { setStatus("failed"); return; }
        setTimeout(poll, 2000);
      } catch (e) {
        setTimeout(poll, 2000);
      }
    };
    poll();
  }, [refresh]);

  return (
    <div className="min-h-screen bg-app flex flex-col items-center justify-center px-5 relative" data-testid="billing-return-page">
      <div className="absolute inset-0 hud-grid opacity-25" />
      <div className="relative glass-strong clip-chamfer brackets p-10 max-w-md w-full text-center">
        {status === "checking" && (
          <>
            <Logo3D size={120} />
            <Loader2 className="w-6 h-6 text-cyan animate-spin mx-auto mt-6" />
            <div className="font-mono text-xs tracking-[0.3em] text-cyan/70 mt-4">VERIFYING TRANSACTION...</div>
          </>
        )}
        {status === "success" && (
          <>
            <CheckCircle2 className="w-16 h-16 text-cyan mx-auto box-glow-cyan rounded-full" />
            <h1 className="font-head text-3xl uppercase font-bold mt-5 text-glow-cyan">Clearance Granted</h1>
            <p className="text-slate-400 mt-3">Your <span className="text-amber">{tierName}</span> clearance is now active.</p>
            <button
              onClick={() => navigate("/simulation")}
              data-testid="billing-continue-btn"
              className="btn-hud mt-7 w-full border border-cyan text-cyan py-3 text-sm box-glow-cyan hover:bg-cyan hover:text-app"
            >
              Enter Simulation
            </button>
          </>
        )}
        {(status === "failed" || status === "timeout") && (
          <>
            <XCircle className="w-16 h-16 text-amber mx-auto" />
            <h1 className="font-head text-3xl uppercase font-bold mt-5 text-amber">
              {status === "timeout" ? "Verification Delayed" : "Transaction Halted"}
            </h1>
            <p className="text-slate-400 mt-3">
              {status === "timeout"
                ? "Payment is still processing. Your clearance will update shortly."
                : "The transaction was not completed."}
            </p>
            <button
              onClick={() => navigate("/pricing")}
              data-testid="billing-retry-btn"
              className="btn-hud mt-7 w-full border border-amber/50 text-amber py-3 text-sm hover:bg-amber hover:text-app"
            >
              Back to Tiers
            </button>
          </>
        )}
      </div>
    </div>
  );
}
